import type { ApiContextData } from "@/services/api/contextTracker";

/** Resource types that can inform “what the user is viewing” in debug UI. */
export const AGENT_ENTITY_RESOURCE_TYPES = new Set([
  "portfolio",
  "holding",
  "performance",
  "exposure",
  "transaction",
  "report",
  "report-config",
  "report-batch",
  "report-clone",
  "document",
  "document-version",
  "product",
  "scenario",
  "worker",
  "worker-type",
  "worker-task",
  "worker-chat",
  "pod",
  "project",
  "playground",
  "cash-flow",
  "watchlist",
  "insight",
  "screener",
]);

export const IGNORED_RESOURCE_TYPES = new Set([
  "in-app-agent",
  "standup",
  "action",
  "metric",
  "news",
  "company",
  "user",
  "generic",
  "dashboard",
]);

export const IGNORED_RESOURCE_IDS = new Set([
  "list",
  "search",
  "filters",
  "columns",
  "main",
  "current",
  "inbox",
  "agentic",
  "chat",
  "overview",
  "profile",
  "refresh",
]);

/** URL path fragments that should never produce a tracked resource (infra / meta APIs). */
export const TRACKER_URL_DENY_SUBSTRINGS = [
  "/agentic/in-app-agent/",
  "/standup/",
  "/metrics",
  "/company",
  "chat-debug-menu",
  "fe-tool-pickers",
  "/users/",
  "/mcp-",
];

export type ActiveResourceEntry = {
  id: string;
  type: string;
  data?: unknown;
  timestamp?: number;
};

const SUB_RESOURCE_PARENTS: Record<string, string[]> = {
  portfolio: ["holding", "transaction", "performance", "exposure", "report"],
  document: ["document-version", "section", "comment"],
  product: ["holding", "performance", "characteristic"],
  screener: ["result", "filter", "criteria"],
  report: ["report-config", "report-batch", "report-clone"],
  worker: ["worker-type", "worker-task", "worker-chat"],
  scenario: ["executive-summary", "master-analysis", "news-flow"],
  pod: ["project", "pod-chat"],
  "cash-flow": ["cash-flow-chat"],
};

export function isSubResource(resourceType: string, parentType: string | undefined): boolean {
  if (!parentType) return false;
  return SUB_RESOURCE_PARENTS[parentType]?.includes(resourceType) ?? false;
}

function hasUsefulResponseData(data: unknown): boolean {
  if (!data || typeof data !== "object") return false;
  const record = data as Record<string, unknown>;
  if (record.count !== undefined) return true;
  if (record.name !== undefined && String(record.name).trim()) return true;
  if (record.title !== undefined && String(record.title).trim()) return true;
  if (record.id !== undefined && String(record.id).trim()) return true;
  return false;
}

function isAllowlistedType(type: string): boolean {
  return AGENT_ENTITY_RESOURCE_TYPES.has(type);
}

/** Map aniOS focused app id to API context resource type for scoping the dev menu list. */
export function resourceTypeForOsFocusedApp(focusedApp: string | undefined): string | undefined {
  if (!focusedApp || focusedApp === "home") return undefined;
  const map: Record<string, string> = {
    portfolio: "portfolio",
    "portfolio-builder": "portfolio",
    screener: "screener",
    dashboard: "dashboard",
    documents: "document",
    product: "product",
    "report-builder": "report",
    scenarios: "scenario",
    worker: "worker",
    "cash-flow": "cash-flow",
    playground: "playground",
    pod: "pod",
    "case-checking": "scenario",
  };
  return map[focusedApp];
}

export function resourceTypeForPathname(pathname: string): string | undefined {
  if (pathname.includes("/portfolio")) return "portfolio";
  if (pathname.includes("/dashboard")) return "dashboard";
  if (pathname.includes("/documents")) return "document";
  if (pathname.includes("/products")) return "product";
  if (pathname.includes("/screener")) return "screener";
  if (pathname.includes("/report-builder") || pathname.includes("/reports")) return "report";
  if (pathname.includes("/scenario-tracker") || pathname.includes("/scenarios")) return "scenario";
  if (pathname.includes("/workers") || pathname.includes("/worker")) return "worker";
  if (pathname.includes("/cash-flow")) return "cash-flow";
  if (pathname.includes("/playground")) return "playground";
  if (pathname.includes("/pod")) return "pod";
  return undefined;
}

export function shouldIgnoreTrackerUrl(url: string): boolean {
  const normalized = url.toLowerCase();
  return TRACKER_URL_DENY_SUBSTRINGS.some((fragment) => normalized.includes(fragment));
}

export function isRelevantActiveResource(entry: ActiveResourceEntry): boolean {
  const { type, id, data } = entry;
  if (!type || !id) return false;
  if (IGNORED_RESOURCE_TYPES.has(type)) return false;
  if (!isAllowlistedType(type)) return false;

  if (IGNORED_RESOURCE_IDS.has(id)) {
    return hasUsefulResponseData(data);
  }

  return true;
}

export function isRelevantApiContextRow(ctx: ApiContextData): boolean {
  if (!ctx.resourceType || !ctx.resourceId) return false;
  return isRelevantActiveResource({
    type: ctx.resourceType,
    id: ctx.resourceId,
    data: ctx.responseData,
    timestamp: ctx.timestamp,
  });
}

export function scopeActiveResourceToFocus(
  resource: ActiveResourceEntry,
  focusResourceType: string | undefined,
): boolean {
  if (!focusResourceType) return true;
  return (
    resource.type === focusResourceType || isSubResource(resource.type, focusResourceType)
  );
}
